import {
  getCognitoErrorCode,
  getCognitoErrorMessage,
  getUnwrappedMigrationMessage,
  isCognitoUserDisabledError,
  isCognitoUserNotFoundError,
} from '@services/cognitoErrorNormalizer';

export const normalizeCognitoSignInErrorMessage = (err) => {
  const code = getCognitoErrorCode(err);
  const message = getCognitoErrorMessage(err);

  // Security/UX: do not reveal that the account exists but is disabled.
  // Treat it like a normal bad-credentials error.
  if (isCognitoUserDisabledError(err)) {
    return 'incorrectUsernameOrPassword';
  }

  const unwrappedMigrationMessage = getUnwrappedMigrationMessage(err);

  const isNotAuthorized =
    code === 'NotAuthorizedException' || /incorrect username or password/i.test(message);

  const isUserNotFound =
    isCognitoUserNotFoundError(err);

  // When a Pre-Authentication trigger rejects the login, Cognito typically surfaces
  // a UserLambdaValidationException with the trigger's error message.
  // Never show this raw internal message to end users.
  const isEmailNotVerified =
    code === 'UserLambdaValidationException' && /EmailNotVerified/i.test(message);

  if (isEmailNotVerified) {
    return 'emailNotVerified';
  }

  // Cognito can surface migration-trigger failures as a generic lambda validation exception.
  // When that happens, we still want to show the same generic login-failure message.
  const isMigrationFailure =
    code === 'UserLambdaValidationException' ||
    code === 'InvalidLambdaResponseException' ||
    /user migration/i.test(message) ||
    (/migration/i.test(message) && /(failed|error)/i.test(message));

  // Security/UX: don't reveal whether the account exists or whether migration failed.
  if (isUserNotFound || isMigrationFailure) {
    return 'incorrectUsernameOrPassword';
  }

  // Non-auth failures (network/rate limit/service issues) -> generic retry message.
  const isRateLimited =
    code === 'TooManyRequestsException' || code === 'LimitExceededException';
  if (isRateLimited) {
    return 'tooManyAttempts';
  }

  const isNetworkIssue = /network|timeout|failed to fetch/i.test(message);
  if (isNetworkIssue) {
    return 'connectionErrorCheckInternet';
  }

  // For normal auth failures, keep Cognito's standard wording.
  if (isNotAuthorized) {
    // Avoid leaking internal details; keep generic wording.
    return 'incorrectUsernameOrPassword';
  }

  // Default: never surface raw Cognito messages on sign-in.
  return 'signInFailed';
};

